import type { SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import {
  expandOfficeHoursToFullWeek,
  type OfficeHourEntry,
} from '../utils/officeHours';

// ============================================================================
// Slot group definitions (exported so parent schemas can reference the same object)
// ============================================================================

export const addressGroupDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'Map URL',
      type: 'url',
      // URL has no defaultField — defaults to usePropertyAddressUrl() at render
      // time. Editor overrides still win.
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Helpers
// ============================================================================

export function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

export type { OfficeHourEntry };

// ============================================================================
// Component
// ============================================================================

interface PropertyInfoPanelProps {
  /** Token prefix of the parent component, e.g. "contactform" or "scheduletourform" */
  prefix: string;
}

/**
 * Shared info panel — Visit Us, Call Us, Office Hours cards.
 * Not a registered slot; imported directly by ContactForm and ScheduleTourForm.
 * The parent component owns the contentSchema and provides lint anchors.
 */
export function PropertyInfoPanel({ prefix }: PropertyInfoPanelProps) {
  const visitLabelPath  = `${prefix}.visit_label`;
  const callLabelPath   = `${prefix}.call_label`;
  const hoursLabelPath  = `${prefix}.hours_label`;
  const addressPath     = `${prefix}.address`;
  const phonePath       = `${prefix}.phone`;
  const officeHoursPath = `${prefix}.office_hours`;

  const visitLabel  = useContentToken(visitLabelPath);
  const callLabel   = useContentToken(callLabelPath);
  const hoursLabel  = useContentToken(hoursLabelPath);
  const address     = useSlotGroup(addressPath, addressGroupDef);
  const phone       = useContentToken(phonePath) || '';
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = address.url || propertyAddressUrl || '';

  const officeHours = useCollectionBinding<OfficeHourEntry>(officeHoursPath, {
    collection: 'office_hours',
  });
  const weekHours = expandOfficeHoursToFullWeek(officeHours);

  const hasHours = weekHours.length > 0;

  return (
    <>
      <div className="cf-info-cards">
        {/* Visit Us — always rendered; property_full_address should always have a value */}
        <div className="cf-card">
          <div className="cf-card-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1C6.13 1 3 4.13 3 8c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1110 5.5a2.5 2.5 0 010 5z" fill="currentColor"/></svg>
          </div>
          <div>
            <EditableToken path={visitLabelPath} as="h3" className="cf-card-label">
              {visitLabel || 'Visit Us'}
            </EditableToken>
            <EditableSlotGroup groupPath={addressPath} definition={addressGroupDef}>
              {addressHref ? (
                <a
                  href={addressHref}
                  target={address._target || '_blank'}
                  rel="noopener noreferrer"
                  className="cf-card-value cf-card-link"
                >
                  {address.text}
                </a>
              ) : (
                <p className="cf-card-value">{address.text}</p>
              )}
            </EditableSlotGroup>
          </div>
        </div>

        <div className="cf-card">
          <div className="cf-card-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 2a2 2 0 00-2 2v1a13 13 0 0013 13h1a2 2 0 002-2v-2.5a1 1 0 00-.68-.95l-3.12-1a1 1 0 00-1.02.27l-1.38 1.38A11 11 0 015.8 7.2l1.38-1.38a1 1 0 00.27-1.02l-1-3.12A1 1 0 005.5 1H4z" fill="currentColor"/></svg>
          </div>
          <div>
            <EditableToken path={callLabelPath} as="h3" className="cf-card-label">
              {callLabel || 'Call Us'}
            </EditableToken>
            <a
              href={`tel:${phone}`}
              className="dniphone dniphonehref cf-card-value cf-card-link"
            >
              <EditableToken path={phonePath} as="span">
                {formatDisplayPhone(phone)}
              </EditableToken>
            </a>
          </div>
        </div>

        {hasHours && (
          <div className="cf-card">
            <div className="cf-card-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 0a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm.5-13H9v6l5.25 3.15.75-1.23-4.5-2.67V5z" fill="currentColor"/></svg>
            </div>
            <div>
              <EditableToken path={hoursLabelPath} as="h3" className="cf-card-label">
                {hoursLabel || 'Office Hours'}
              </EditableToken>
              <div className="cf-hours-grid">
                {weekHours.map((h, i) => (
                  <div key={i} className="cf-hours-row">
                    <span className="cf-hours-day">{h.day}</span>
                    <span className="cf-hours-time">
                      {h.open_time && h.close_time
                        ? `${formatTime(h.open_time)} – ${formatTime(h.close_time)}`
                        : 'Closed'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* ===== Info cards ===== */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .cf-info-cards {
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          overflow: hidden;
        }
        .cf-card {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        .cf-card:last-child { border-bottom: none; }
        .cf-card-icon {
          flex-shrink: 0;
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          color: var(--vibe-color-primary);
          margin-top: 1px;
        }
        .cf-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin: 0 0 0.25rem;
        }
        .cf-card-value {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-card-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .cf-card-link:hover { color: var(--vibe-color-primary); }

        .cf-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          margin-top: 0.125rem;
        }
        .cf-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          line-height: 1.6;
        }
        .cf-hours-day {
          color: var(--vibe-color-text);
          font-weight: 500;
          min-width: 5.5rem;
        }
        .cf-hours-time {
          color: var(--vibe-color-text-muted);
          text-align: right;
        }
      `}</style>
    </>
  );
}
